import {
    TaxCardMainTableError,
    TaxCardMainTableNoEmployees,
    TaxCardMainTableSvgContainer, // this one is intriguing.. are we actually using good old SVG now?  Apparently yes!
} from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/TaxCardMainTableSvg';
import { LoadMoreTarget } from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/LoadMoreTarget';
import { RowExpandButton, TaxCardRow } from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/TaxCardRow';
import { EmployeeDTO } from '@Page/SalaryTaxCard/SalaryTaxCard.types';
import { Icon, Input, Spinner } from '@tlx/atlas';
import React, { useEffect } from 'react';
import useOpenController from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/useOpenController';
import { EmployeeTaxcardsInfo } from '@Page/SalaryTaxCard/hooks/useSalaryTaxCardForReal';

import { useIntl } from 'react-intl';
import { TaxcardSelectableMachinery } from '@Page/SalaryTaxCard/hooks/useTaxcardSelectableEmployees';
export type ColumnType = keyof typeof ColumnNames;

//should match ColumnNamesSorting in the backend APIService
export enum ColumnNames {
    EMPLOYEE_NAME = 'DISPLAYNAME',
    HAS_TAXCARD = 'STATUS',
    DEDUCTION_CODE = 'DEDUCTIONCODE',
    TAXCARD_TYPE = 'TYPE',
    DEDUCTION_TABLE_NUMBER = 'TABLENUMBER',
    TAX_PERCENTAGE = 'PERCENTAGE',
    TAX_FREE_AMOUNT = 'FREEAMOUNT',
    TAXCARD_ADDITIONAL_INFO = 'ADDITIONALINFORMATION',
    TAXCARD_LAST_CHANGED = 'DATE',
}

export interface TaxCardMainTableProps {
    employeeTaxcards?: EmployeeTaxcardsInfo;
    quitEmployeeTaxcards?: EmployeeTaxcardsInfo;
    columns: string[];
    sort: string;
    toggleSort: (column: string) => void;
    skeleton?: boolean;
    lastUpdatedOrderId?: number;
    taxcardSelectEmployeesMachinery: TaxcardSelectableMachinery;
    taxcardSelectQuitEmployeesMachinery: TaxcardSelectableMachinery;
}

export function TaxCardMainTable({
    employeeTaxcards,
    quitEmployeeTaxcards,
    columns,
    sort,
    toggleSort,
    skeleton,
    lastUpdatedOrderId,
    taxcardSelectEmployeesMachinery,
    taxcardSelectQuitEmployeesMachinery,
}: TaxCardMainTableProps) {
    const { isOpen: isOpenRows, toggle: toggleRows, open: openRows, close: closeRows } = useOpenController(true);

    const {
        isOpen: isOpenQuitRows,
        toggle: toggleQuitRows,
        open: openQuitRows,
        close: closeQuitRows,
    } = useOpenController(true);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.target as HTMLElement).tagName === 'INPUT') {
                return;
            }
            if (event.key === 'd') {
                if (isOpenRows) {
                    closeRows();
                    closeQuitRows();
                } else {
                    openRows();
                    openQuitRows();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpenRows, closeRows, closeQuitRows, openRows, openQuitRows]);

    const [sortProp, sortingDirection] = sort.split('_', 2);

    const intl = useIntl();

    return (
        <>
            <table className="atl-table">
                <thead className="atl-thead taxCardMainTable--sticky">
                    <tr className="header-row">
                        <td className="atl-td atl-whitespace-nowrap" />
                        <td className="atl-th" />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.EMPLOYEE_NAME}
                            columnText={intl.formatMessage({ id: 'text_name' })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.HAS_TAXCARD}
                            columnText={intl.formatMessage({
                                id: 'text_status',
                            })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.DEDUCTION_CODE}
                            columnText={intl.formatMessage({
                                id: 'text_advance_tax_decution_deduction_code',
                            })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.TAXCARD_TYPE}
                            columnText={intl.formatMessage({ id: 'text_type' })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.DEDUCTION_TABLE_NUMBER}
                            columnText={intl.formatMessage({
                                id: 'text_advance_tax_decution_table_number',
                            })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.TAX_PERCENTAGE}
                            columnText={intl.formatMessage({
                                id: 'text_percentage',
                            })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.TAX_FREE_AMOUNT}
                            columnText={intl.formatMessage({
                                id: 'text_advance_tax_decution_free_amount',
                            })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.TAXCARD_ADDITIONAL_INFO}
                            columnText={intl.formatMessage({
                                id: 'text_additional_info',
                            })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                        <HeaderColumn
                            columns={columns}
                            column={ColumnNames.TAXCARD_LAST_CHANGED}
                            columnText={intl.formatMessage({
                                id: 'text_last_edited',
                            })}
                            toggleSort={toggleSort}
                            sortProp={sortProp}
                            sortingDirection={sortingDirection}
                        />
                    </tr>
                </thead>
                <tbody>
                    {/* Showing the rows of QUITTERS employees*/}
                    {quitEmployeeTaxcards ? (
                        <>
                            <TaxCardTableSubtitle
                                text={intl.formatMessage({
                                    id: 'text_employees_that_quit',
                                })}
                                open={isOpenQuitRows}
                                toggle={toggleQuitRows}
                                className="border-bottom-grey-20"
                                taxcardSelectEmployeesMachinery={taxcardSelectQuitEmployeesMachinery} // KKFIXME hadde vært fint om dette funket
                            />
                            {isOpenQuitRows ? (
                                <>
                                    <TaxCardTable
                                        employeeTaxcards={quitEmployeeTaxcards}
                                        columns={columns}
                                        skeleton={skeleton}
                                        lastUpdatedOrderId={lastUpdatedOrderId}
                                        taxcardSelectEmployeesMachinery={taxcardSelectQuitEmployeesMachinery} // KKFIXME hadde vært fint om dette funket
                                    />
                                    <TableState {...quitEmployeeTaxcards} />
                                </>
                            ) : (
                                <></>
                            )}
                        </>
                    ) : (
                        <></> //this looks like a fish to me
                    )}
                    {/* Showing the rows of those who have not quit*/}
                    <TaxCardTableSubtitle
                        text={intl.formatMessage({ id: 'text_employees' })}
                        open={isOpenRows}
                        toggle={toggleRows}
                        taxcardSelectEmployeesMachinery={taxcardSelectEmployeesMachinery}
                    />
                    {isOpenRows ? (
                        <>
                            <TaxCardTable
                                employeeTaxcards={employeeTaxcards}
                                columns={columns}
                                skeleton={skeleton}
                                lastUpdatedOrderId={lastUpdatedOrderId}
                                taxcardSelectEmployeesMachinery={taxcardSelectEmployeesMachinery}
                            />
                            <TableState {...employeeTaxcards} />
                        </>
                    ) : (
                        <></>
                    )}
                    {isOpenRows && employeeTaxcards?.data.length != 0 ? <tr style={{ height: '40px' }}></tr> : <></>}
                </tbody>
            </table>
            <LoadMoreTarget hasMore={employeeTaxcards?.hasMore} onLoadMore={employeeTaxcards?.loadMore} />
        </>
    );
}

type HeaderProps = {
    columns: string[];
    column: string;
    columnText: string;
    toggleSort: (column: string) => void;
    sortProp: string;
    sortingDirection: string;
};

export function HeaderColumn({ columns, column, columnText, toggleSort, sortProp, sortingDirection }: HeaderProps) {
    return (
        <>
            {columns.includes(column) && (
                <th
                    className="atl-th atl-text-left atl-font-medium taxCardMainTable-table__cell--highlight taxCardMainTable-cursor-pointer"
                    onClick={() => toggleSort(column)}
                    id={`column-${column}`}
                >
                    <SortableHeader
                        text={columnText}
                        sortingDirection={sortProp == column ? sortingDirection : ''}
                        className={
                            column == ColumnNames.EMPLOYEE_NAME || column == ColumnNames.DEDUCTION_CODE
                                ? 'taxCardMainTable-table__20ch'
                                : ''
                        }
                    />
                </th>
            )}
        </>
    );
}

export interface TaxcardState {
    data?: EmployeeDTO[];
    error?: Error;
    isLoading?: boolean;
    hasMore?: boolean;
    loadMore?: () => void;
    refresh?: () => void;
}

function TableState(taxcards: TaxcardState) {
    const intl = useIntl();
    let tbodyContent: React.ReactNode;
    if (taxcards?.isLoading) {
        tbodyContent = (
            <>
                <Spinner />
                {intl.formatMessage({ id: 'text_loading' })}
            </>
        );
    } else if (taxcards?.error) {
        tbodyContent = (
            <>
                <TaxCardMainTableError />
                {intl.formatMessage({ id: 'heading_unexpected_error' })}
            </>
        );
    } else if (taxcards?.data?.length === undefined || taxcards?.data?.length == 0) {
        tbodyContent = (
            <>
                <TaxCardMainTableNoEmployees />
                {intl.formatMessage({ id: 'text_no_employees_found' })}
            </>
        );
    } else {
        return <></>;
    }
    return (
        <tr className="atl-tr">
            <td className="atl-td atl-text-center atl-py-24" colSpan={100}>
                <TaxCardMainTableSvgContainer>{tbodyContent}</TaxCardMainTableSvgContainer>
            </td>
        </tr>
    );
}

export interface TaxCardTableProps {
    employeeTaxcards?: EmployeeTaxcardsInfo;
    columns: string[];
    skeleton?: boolean;
    lastUpdatedOrderId?: number;
    taxcardSelectEmployeesMachinery: TaxcardSelectableMachinery;
}

function TaxCardTable({
    employeeTaxcards,
    columns,
    skeleton,
    lastUpdatedOrderId,
    taxcardSelectEmployeesMachinery, // KKFIXME her er det noe gruff, EVENTUELT det funker rett ut av boksen
}: TaxCardTableProps) {
    return (
        <>
            {employeeTaxcards?.data.map((employee: EmployeeDTO) => {
                return (
                    <TaxCardRow
                        key={employee.id}
                        employee={employee}
                        columns={columns}
                        skeleton={skeleton}
                        lastUpdatedOrderId={lastUpdatedOrderId}
                        taxcardSelectEmployeesMachinery={taxcardSelectEmployeesMachinery}
                    />
                );
            })}
        </>
    );
}

interface TaxCardTableSubtitleProps {
    text: string;
    open: boolean;
    toggle: () => void;
    className?: string;
    taxcardSelectEmployeesMachinery: TaxcardSelectableMachinery;
}

function TaxCardTableSubtitle({
    text,
    open,
    toggle,
    className,
    taxcardSelectEmployeesMachinery,
}: TaxCardTableSubtitleProps) {
    return (
        <tr
            className={`atl-tr ${className ?? ''}`}
            // comment out this because it triggers also when the user simply clicks the checkbox (to its side). But an alternative might be to prevent the click event from bubbling up from the checkbox perhaps. [Kristoffer Kvello, 2022-08-24]
            // onClick={toggle}
        >
            <td className="checkbox-row atl-td">
                <Input
                    type="checkbox"
                    checked={taxcardSelectEmployeesMachinery.allChecked}
                    onChange={(e) => {
                        if (e.target.checked) {
                            // choose all employees
                            taxcardSelectEmployeesMachinery.onCheckAll();
                        } else {
                            // choose none
                            taxcardSelectEmployeesMachinery.onUncheckAll();
                        }
                    }}
                    data-testid={`taxcardrow-all-none-employees-${text}-checkbox`}
                />
            </td>

            <td>
                <RowExpandButton open={open} toggleRowExpansion={toggle} />
            </td>
            <td className="atl-th atl-text-left" style={{ paddingLeft: 10 }} colSpan={10}>
                {text}
            </td>
        </tr>
    );
}

interface SortableHeaderProps {
    text: string;
    sortingDirection: string;
    className: string;
}

function SortableHeader({ sortingDirection, text, className }: SortableHeaderProps) {
    let iconName;
    switch (sortingDirection) {
        case 'DESC':
            iconName = 'expand_more';
            break;
        case 'ASC':
            iconName = 'expand_less';
            break;
        default:
            iconName = 'unfold_more';
            break;
    }
    return (
        <div className="sortable-header__container">
            <span className={`taxCardMainTable-table__cell--highlight__text ${className ?? ''}`}>{text}</span>
            <span className="toggleArrowContainer">
                <Icon className={`toggleArrow ${sortingDirection ? '' : 'toggleArrowHidden'}`}>{iconName}</Icon>
            </span>
        </div>
    );
}
